import { Box, Grid, IconButton, Typography } from '@mui/material';
import { Field, Form, Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { TextField } from '../../../../components/formik/text-field';
import { LoadingButton } from '@mui/lab';
import { useToggleTwoverifyMutation } from '../../api/user.api';
import { handleServerErrors } from 'utils/utils';
import { immediateToast } from 'utils/toast';
import useProfile from 'hooks/use-update-profile-query';
import { useState } from 'react';
import { Visibility, VisibilityOff } from '@mui/icons-material';

const TwoVerfiyFields: Array<{
    name: string;
    label: string;
    icon: any;
}> = [
    {
        name: 'password',
        label: 'رمز عبور دومرحله‌ای',
        icon: <i className="fa-regular fa-lock" />,
    },
    {
        name: 'repeat_password',
        label: 'تکرار رمز عبور دومرحله‌ای',
        icon: <i className="fa-solid fa-lock" />,
    },
];

type TwoVerfiyFormType = {
    is_active: boolean;
    password: string;
    repeat_password: string;
};

const TwoVerfiyForm = ({ onClose }: any) => {
    const [toggleTwoverify, { isLoading }] = useToggleTwoverifyMutation();
    const { update, profile } = useProfile();
    const [showPassword, setShowPassword] = useState(false);
    const [showRepeatPassword, setShowRepeatPassword] = useState(false);
    const isEnabling = !profile?.twoverify;

    const TwoVerfiySchema = Yup.object().shape({
        password: isEnabling
            ? strongPasswordValidation()
            : Yup.string().required('رمز عبور دومرحله‌ای را وارد کنید.'),
        repeat_password: isEnabling
            ? Yup.string()
                  .required('تکرار رمز عبور دومرحله‌ای الزامی است.')
                  .oneOf(
                      [Yup.ref('password')],
                      'رمز عبور دومرحله‌ای و تکرار آن یکسان نیستند.',
                  )
            : Yup.string().nullable(),
    });

    const handleSubmit = (
        values: TwoVerfiyFormType,
        { setStatus, setSubmitting }: FormikHelpers<TwoVerfiyFormType>,
    ) => {
        toggleTwoverify({
            is_active: values.is_active,
            password: values.password,
            repeat_password: values.repeat_password,
        })
            .unwrap()
            .then((res) => {
                if (!profile) return;

                immediateToast('success', {
                    message: res.two_factor_enabled
                        ? 'تایید دومرحله‌ای فعال شد'
                        : 'تایید دومرحله‌ای غیرفعال شد',
                    timeout: 2000,
                });

                update({
                    ...profile,
                    twoverify: res.two_factor_enabled,
                });

                onClose();
            })
            .catch((err) => {
                handleServerErrors(err, setStatus);
            })
            .finally(() => setSubmitting(false));
    };

    const PasswordInputField = ({
        name,
        isVisible,
    }: {
        name: 'password' | 'repeat_password';
        isVisible: boolean;
    }) => {
        const label =
            name === 'password'
                ? 'رمز عبور دومرحله‌ای'
                : 'تکرار رمز عبور دومرحله‌ای';

        return (
            <Field
                type={isVisible ? 'text' : 'password'}
                component={TextField}
                name={name}
                label={label}
                ltr="true"
                liveValidation={false}
                icon={<i className="fa-regular fa-lock" />}
                secondIcon={
                    <IconButton
                        onClick={() =>
                            name === 'password'
                                ? setShowPassword((show) => !show)
                                : setShowRepeatPassword((show) => !show)
                        }
                        onMouseDown={(e) => e.preventDefault()}
                        aria-label={isVisible ? 'مخفی کردن رمز' : 'نمایش رمز'}
                        sx={{
                            width: 34,
                            height: 34,
                            p: 0.75,
                            m: 0,
                            color: 'text.secondary',
                            '&:hover': { color: 'text.primary' },
                        }}
                    >
                        {isVisible ? (
                            <VisibilityOff sx={{ color: 'text.primary' }} />
                        ) : (
                            <Visibility sx={{ color: 'text.primary' }} />
                        )}
                    </IconButton>
                }
                InputProps={{ autoComplete: 'new-password' }}
                inputProps={{ maxLength: 64, dir: 'ltr' }}
                sx={{
                    '& .MuiOutlinedInput-root': {
                        minHeight: 50,
                        backgroundColor: 'background.paper',
                    },
                    '& .MuiOutlinedInput-input': {
                        py: 1.4,
                        letterSpacing: isVisible ? 0.2 : 1.2,
                    },
                }}
            />
        );
    };

    return (
        <Formik<TwoVerfiyFormType>
            initialValues={{
                is_active: isEnabling,
                password: '',
                repeat_password: '',
            }}
            onSubmit={handleSubmit}
            validationSchema={TwoVerfiySchema}
            validateOnChange={false}
            validateOnBlur={false}
        >
            <Form>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Typography
                            variant="body2"
                            sx={{ color: 'text.secondary', lineHeight: 2 }}
                        >
                            {isEnabling
                                ? 'یک رمز عبور مستقل برای تایید دومرحله‌ای تعیین کنید. این رمز هنگام ورود، بعد از کد پیامکی درخواست می‌شود.'
                                : 'برای غیرفعال‌سازی، رمز عبور دومرحله‌ای فعلی خود را وارد کنید.'}
                        </Typography>
                    </Grid>

                    <Grid item xs={12}>
                        <PasswordInputField
                            name="password"
                            isVisible={showPassword}
                        />
                        {isEnabling && (
                            <Box
                                sx={{
                                    mt: 1,
                                    px: 0.5,
                                    color: 'text.secondary',
                                    fontSize: 11.5,
                                    lineHeight: 1.9,
                                }}
                            >
                                حداقل ۸ کاراکتر و شامل حرف انگلیسی، عدد و کاراکتر ویژه باشد.
                            </Box>
                        )}
                    </Grid>

                    {isEnabling && (
                        <Grid item xs={12}>
                            <PasswordInputField
                                name="repeat_password"
                                isVisible={showRepeatPassword}
                            />
                        </Grid>
                    )}

                    <Grid item xs={12}>
                        <LoadingButton
                            fullWidth
                            variant="contained"
                            color={isEnabling ? 'primary' : 'error'}
                            loading={isLoading}
                            type="submit"
                        >
                            {isEnabling
                                ? 'فعال‌سازی تایید دومرحله‌ای'
                                : 'غیرفعال‌سازی تایید دومرحله‌ای'}
                        </LoadingButton>
                    </Grid>
                </Grid>
            </Form>
        </Formik>
    );
};

export { TwoVerfiyForm, TwoVerfiyFields };

const strongPasswordValidation = () =>
    Yup.string()
        .required('رمز عبور دومرحله‌ای الزامی است.')
        .matches(
            /^(?=.*[A-Za-z])(?=.*[0-9])(?=.*[\x21-\x2F\x3A-\x40\x5B-\x60\x7B-\x7E])[\x21-\x7E]+$/,
            'رمز عبور باید شامل حداقل یک حرف انگلیسی، یک عدد و یک کاراکتر ویژه باشد.',
        )
        .min(8, 'رمز عبور باید حداقل ۸ کاراکتر باشد.')
        .max(64, 'رمز عبور نباید بیش از ۶۴ کاراکتر باشد.');
